import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ThemeToggle from './components/ThemeToggle';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDemoLogin = () => {
    setEmail('demo@frankenfalter.ch');
    setPassword('Demo2024');
    // Trigger form submission after state update
    setTimeout(() => {
      document.querySelector('form').requestSubmit();
    }, 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-800 to-secondary-950 px-4 py-8">
      {/* Theme Toggle - Fixed Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* Login Container */}
      <div className="w-full max-w-md">
        {/* Brand/Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-primary-600 rounded-2xl shadow-lg mb-4">
            <svg width="58" height="58" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Stack of Banknotes - Side View */}

              {/* Bottom note */}
              <rect x="20" y="65" width="60" height="3" rx="0.5" fill="white" opacity="0.5"/>

              {/* Note layers building up */}
              <rect x="20" y="62" width="60" height="3" rx="0.5" fill="white" opacity="0.55"/>
              <rect x="20" y="59" width="60" height="3" rx="0.5" fill="white" opacity="0.6"/>
              <rect x="20" y="56" width="60" height="3" rx="0.5" fill="white" opacity="0.65"/>
              <rect x="20" y="53" width="60" height="3" rx="0.5" fill="white" opacity="0.7"/>
              <rect x="20" y="50" width="60" height="3" rx="0.5" fill="white" opacity="0.75"/>
              <rect x="20" y="47" width="60" height="3" rx="0.5" fill="white" opacity="0.8"/>
              <rect x="20" y="44" width="60" height="3" rx="0.5" fill="white" opacity="0.85"/>

              {/* Top note - full face */}
              <rect x="20" y="30" width="60" height="14" rx="1.5" fill="white" stroke="#1e40af" strokeWidth="1.5" opacity="0.5"/>

              {/* Side edge of stack */}
              <rect x="20" y="44" width="60" height="21" fill="white" opacity="0.9"/>

              {/* Right edge lines to show thickness */}
              <line x1="80" y1="44" x2="80" y2="65" stroke="#1e40af" strokeWidth="1.5" opacity="0.6"/>
              <line x1="20" y1="44" x2="20" y2="65" stroke="#1e40af" strokeWidth="1.5" opacity="0.6"/>

              {/* Top note face */}
              <rect x="20" y="30" width="60" height="14" rx="1.5" fill="white" stroke="#1e40af" strokeWidth="1.5" opacity="0.5"/>
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Frankenfalter
          </h1>
          <p className="text-sm md:text-base text-secondary-300">
            Ihr persönlicher Vermögenstracker
          </p>
        </div>

        {/* Login Card */}
        <div className="card shadow-xl">
          <div className="card-header">
            <h2 className="text-xl md:text-2xl font-semibold text-secondary-900 text-center">
              Willkommen zurück
            </h2>
          </div>

          <div className="card-body space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-error-50 border border-error-200 rounded-lg text-error-700">
                <svg
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label className="label">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="ihre.email@beispiel.ch"
                  className="input"
                  disabled={loading}
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="label">
                  Passwort
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="input"
                  disabled={loading}
                />
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className="w-full btn btn-primary text-base md:text-lg font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Anmeldung läuft...
                  </span>
                ) : (
                  'Anmelden'
                )}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-secondary-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-secondary-500">oder</span>
                </div>
              </div>

              {/* Demo Account Button */}
              <button
                type="button"
                onClick={handleDemoLogin}
                disabled={loading}
                className="w-full btn btn-success text-base md:text-lg font-semibold flex items-center justify-center gap-2"
              >
                <span>🎯</span>
                <span>Demo Account ausprobieren</span>
              </button>

              {/* Register Link */}
              <div className="text-center text-sm text-secondary-600">
                Noch kein Konto?{' '}
                <Link
                  to="/register"
                  className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Jetzt registrieren
                </Link>
              </div>

              {/* Privacy Link */}
              <div className="text-center pt-2">
                <Link
                  to="/datenschutz"
                  className="text-xs text-secondary-500 hover:text-secondary-700 transition-colors"
                >
                  Datenschutzerklärung
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-xs text-secondary-600">
            © {new Date().getFullYear()} HEDIT GmbH
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
